import clsx from "clsx";
import { useTranslation } from "next-i18next";
import { SyntheticEvent, useEffect, useMemo } from "react";
import { currency } from "utils";
import CartLineItem from "@Components/cartLineItem/CartLineItem";
import { Button } from "@Components/ui";
import { DY_PAGE_CONTEXT } from "@Constants/common";
import { STORAGE_KEYS, STORAGE_TYPES } from "@Constants/storage";
import { setDYContext } from "@Libs/dynamic-yield/dynamicYield";
import { getItem } from "@Utils/browserStorage";
import { convertToDollar } from "@Utils/currency";
import { isCanadianLocale } from "@Utils/locale";
import Link from "@wff/components/Link";
import { useCustomer } from "@wff/hooks/useCustomer";
import { ICartLineItem } from "@wff/interfaces";
import { cartSliceSelector } from "@wff/store/cartSlice";
import { useAppSelector, useAppDispatch } from "@wff/store/hooks";
import { localeSelector } from "@wff/store/localeSlice";
import { ZipCodeSelector, toggleZipCodePopUp } from "@wff/store/zipcodeSlice";
import classes from "./Cart.module.scss";

const Cart = () => {
  const { t } = useTranslation("common");
  const { customerInfo } = useCustomer();
  const cartSlice = useAppSelector(cartSliceSelector);
  const currentLocale = useAppSelector(localeSelector);
  const { zipcode } = useAppSelector(ZipCodeSelector);
  const dispatch = useAppDispatch();
  const { isPickupAvailable, availability } = useAppSelector(
    (state) => state?.zipcode
  );

  const memoizeSKUs = useMemo(
    () => cartSlice?.lineItems?.map((lineItem) => lineItem?.sku),
    [cartSlice]
  );
  //Set 'CART' DY-PageContext
  useEffect(() => {
    setDYContext(
      DY_PAGE_CONTEXT.CART,
      currentLocale?.languageCode,
      memoizeSKUs || [""]
    );
  }, [memoizeSKUs, currentLocale?.languageCode]);

  const orderMinimumAmount = convertToDollar(
    cartSlice?.shippingEligibility?.eligibilityThreshold
  );
  const amountToReachOrderMinimum = convertToDollar(
    cartSlice?.shippingEligibility?.eligibilityAmountRemaining
  );
  const showOrderMinimumUnmetSection =
    !Number.isNaN(Number(amountToReachOrderMinimum)) &&
    Number(amountToReachOrderMinimum) > 0;
  const cartIsEmpty = cartSlice.lineItems?.length === 0;
  const subTotal = isCanadianLocale(currentLocale.languageCode)
    ? cartSlice?.rawSubTotal
    : cartSlice?.subTotal;

  const handleRedirect = () => {
    if (customerInfo?.email) {
      return `/${currentLocale.languageCode}/checkout?step=1`;
    }
    return `/${currentLocale.languageCode}/checkout`;
  };

  const validateZipCode = (event: SyntheticEvent) => {
    if (!zipcode && isCanadianLocale(currentLocale.languageCode)) {
      event.preventDefault();
      dispatch(toggleZipCodePopUp());
    }
  };

  const zipCodeAvailabilityInLocalStorageForCA = getItem(
    STORAGE_KEYS.CUSTOMER_ZIP_AVAILABILITY_CA,
    STORAGE_TYPES.LOCAL
  );
  const isDeliveryAvailable =
    availability || zipCodeAvailabilityInLocalStorageForCA === "true";

  const isCheckoutButtonEnabled =
    (isPickupAvailable || isDeliveryAvailable) && !showOrderMinimumUnmetSection;

  return (
    <div className={classes["cart"]}>
      <h1
        className={clsx(
          classes["cart-title"],
          cartIsEmpty && classes["cart-title--center"]
        )}
      >
        {t("cart.general.title")}
      </h1>
      {!cartIsEmpty ? (
        <div>
          <div className={classes["cart-content"]}>
            <div>
              {cartSlice.lineItems?.map(
                (cartItem: ICartLineItem, index: number) => {
                  return (
                    <CartLineItem
                      key={index}
                      item={cartItem}
                      isCart={true}
                      isFlyOut={false}
                      isReadOnly={false}
                      className={classes["cart-content-item"]}
                    />
                  );
                }
              )}
            </div>
            <div className={classes["cart-content-sidebar"]}>
              <div className={classes["cart-content-delivery"]}>
                {showOrderMinimumUnmetSection && (
                  <p className={classes["cart-content-delivery-amount"]}>
                    {t("checkout.minimum_unmet.add_text")}
                    <span className="red--text">
                      {currency.format(amountToReachOrderMinimum)}
                    </span>
                    <span>{t("checkout.minimum_unmet.minimum_text")}</span>
                    <span className="red--text">
                      {currency.format(orderMinimumAmount)}
                    </span>
                  </p>
                )}
                {!showOrderMinimumUnmetSection && isDeliveryAvailable && (
                  <p className={classes["cart-content-delivery-message"]}>
                    {t("cart.ship.free_delivery")}
                  </p>
                )}
              </div>
              <div className={classes["cart-content-main"]}>
                <div className={classes["cart-content-main-total"]}>
                  <span className={classes["cart-content-main-total-price"]}>
                    {t("cart.general.subtotal")}
                  </span>
                  <span className={classes["cart-content-main-total-price"]}>
                    {currency.format(convertToDollar(subTotal))}
                  </span>
                </div>
                <div className={classes["cart-content-actions"]}>
                  <Link href={handleRedirect()}>
                    <a
                      data-testid="cart-checkout-action"
                      className={` ${
                        !isCheckoutButtonEnabled
                          ? classes["cart-content-actions-checkout-pointer-a"]
                          : ""
                      }`}
                    >
                      <Button
                        className={`${
                          classes["cart-content-actions-checkout"]
                        } ${
                          !isCheckoutButtonEnabled
                            ? classes["cart-content-actions-checkout-pointer"]
                            : ""
                        }`}
                        onClick={validateZipCode}
                      >
                        {t("cart.general.checkout")}
                      </Button>
                    </a>
                  </Link>
                  <Link href={`/${currentLocale.languageCode}`}>
                    <span className={classes["cart-content-actions-continue"]}>
                      {t("cart.general.continue_shopping")}
                    </span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={classes["cart-empty"]}>
          <p className={classes["cart-empty-message"]}>
            {t("cart.general.empty")}
          </p>
          <Link href={`/${currentLocale.languageCode}`}>
            <Button
              className={`${classes["cart-content-actions-checkout"]}  ${classes["cart-empty-button"]}`}
            >
              {t("cart.general.continue_shopping")}
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default Cart;
